import { isHexColor } from './article-html'

/**
 * The colour-palette block's data model and its HTML round trip.
 *
 * The palette lives inside the article's `content` HTML rather than in a column,
 * so this serialization IS the storage format. Two consequences worth knowing:
 *
 * - It must be **self-describing**: every palette name and swatch colour has to
 *   be reconstructible from the markup alone, with no schema to consult.
 * - Changing it means migrating stored articles, so the round trip is covered by
 *   tests rather than trusted.
 *
 * A palette's name is carried as real text (a `<p>`), not as an attribute value,
 * so a reader gets selectable, accessible text and there is exactly one place the
 * name lives.
 */

export interface ArticlePalette {
	name: string
	/** Hex colours, in the author's order. May be empty: an empty palette is valid. */
	colors: string[]
}

/** The template's starting palettes. */
export const DEFAULT_PALETTE_NAMES = ['Main palette', 'Secondary palette'] as const

/** What a new swatch starts as, so a freshly added chip is visible rather than invisible-white. */
export const DEFAULT_SWATCH_COLOR = '#000000'

/**
 * Build the palette block as real DOM.
 *
 * The ONE definition of the stored markup. Both writers go through it: the
 * template seeds an article by serializing to a string, and the editor node
 * returns this element from its `renderHTML`. Two hand-written copies of a
 * storage format drift, and drift here means articles that no longer parse.
 *
 * A swatch carries its colour in `data-swatch` — which the sanitizer validates as
 * a colour and turns into a background — and repeats it as text, so the hex code
 * is readable next to the colour itself.
 */
export function buildPaletteElement(palettes: readonly ArticlePalette[]): HTMLElement {
	const block = document.createElement('div')
	block.setAttribute('data-article-palette', '')

	for (const palette of palettes) {
		const group = document.createElement('div')
		group.setAttribute('data-palette', '')

		// The name is real text, not an attribute: a reader gets selectable,
		// accessible text and the name lives in exactly one place.
		const name = document.createElement('p')
		name.textContent = palette.name
		group.append(name)

		for (const color of palette.colors.filter(isHexColor)) {
			const swatch = document.createElement('span')
			swatch.setAttribute('data-swatch', color)
			swatch.textContent = color
			group.append(swatch)
		}

		block.append(group)
	}

	return block
}

/** Serialize palettes to the stored markup. */
export function serializePalettes(palettes: readonly ArticlePalette[]): string {
	return buildPaletteElement(palettes).outerHTML
}

/**
 * Read palettes back out of a palette element. Tolerant on purpose: a swatch
 * whose colour is not a hex value is skipped rather than failing the whole parse,
 * because a half-readable article is better than none.
 */
export function parsePaletteElement(element: Element): ArticlePalette[] {
	return Array.from(element.querySelectorAll('[data-palette]')).map((group) => {
		const colors = Array.from(group.querySelectorAll('[data-swatch]'))
			.map((swatch) => swatch.getAttribute('data-swatch'))
			.filter(isHexColor)
		return { name: group.querySelector('p')?.textContent?.trim() ?? '', colors }
	})
}

/** Read palettes out of an HTML string. Returns `[]` when it holds no palette block. */
export function parsePalettes(html: string): ArticlePalette[] {
	const template = document.createElement('template')
	template.innerHTML = html
	const block = template.content.querySelector('[data-article-palette]')
	return block ? parsePaletteElement(block) : []
}

/** The palettes a fresh brand template starts with: named, each with one swatch. */
export function defaultPalettes(): ArticlePalette[] {
	return DEFAULT_PALETTE_NAMES.map((name) => ({ name, colors: [DEFAULT_SWATCH_COLOR] }))
}

/**
 * Normalize a value typed into a hex field: adds the missing `#`, lowercases, and
 * returns `null` when it is not a colour — which callers treat as "leave the
 * swatch's colour alone" rather than clearing it.
 */
export function normalizeHexInput(value: string): string | null {
	const candidate = value.trim().startsWith('#') ? value.trim() : `#${value.trim()}`
	return isHexColor(candidate) ? candidate.toLowerCase() : null
}
